
import { useState } from "react"
import { Send } from "lucide-react"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { ScrollArea } from "./ui/scroll-area"
import { Badge } from "./ui/badge"

function OrgEQIcon({ size = 40 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <polygon
        points="50,2 98,27 98,73 50,98 2,73 2,27"
        fill="none"
        stroke="#6FC276"
        strokeWidth="4"
        strokeLinejoin="round"
      />
      <path
        d="M8,50 L20,50 L25,60 L50,40 L70,55 L92,55"
        fill="none"
        stroke="#6FC276"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

interface Message {
  id: string
  content: string
  sender: "user" | "assistant"
  timestamp: Date
}

export function OrgTeamSelector() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content:
        "Hi! I'm here to help you understand your team's emotional dynamics and organizational insights. What would you like to explore?",
      sender: "assistant",
      timestamp: new Date(),
    },
  ])
  const [inputValue, setInputValue] = useState("")

  const promptSuggestions = [
    "What are the current stress levels across teams?",
    "Show me recent emotional trends",
    "Which teams need attention right now?",
    "How is team collaboration performing?",
    "What are the top friction points?",
    "Analyze productivity vs emotional state",
  ]

  const handleSendMessage = () => {
    if (!inputValue.trim()) return

    const newMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      sender: "user",
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, newMessage])
    setInputValue("")

    setTimeout(() => {
      const assistantResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: `I understand you're asking about "${inputValue}". Based on your organization's data, I can provide insights on emotional patterns, team dynamics, and specific metrics. Let me analyze the current state and trends for you.`,
        sender: "assistant",
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, assistantResponse])
    }, 1000)
  }

  const handlePromptClick = (prompt: string) => {
    setInputValue(prompt)
  }

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsModalOpen(true)}
          className="w-16 h-16 rounded-full bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 border border-gray-200 flex items-center justify-center p-2"
        >
          <OrgEQIcon size={60} />
        </Button>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[95vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b border-border">
            <DialogTitle className="flex items-center gap-3">
              <OrgEQIcon size={28} />
              <span className="text-xl">Ask About Your Team</span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 flex flex-col min-h-0">
            <ScrollArea className="flex-1 px-6 py-2 min-h-[300px]">
              <div className="space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-3 ${
                        message.sender === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {messages.length <= 1 && (
              <div className="px-6 py-2 border-t border-border bg-muted/30">
                <p className="text-sm font-medium text-muted-foreground mb-2">Quick questions to get started:</p>
                <div className="flex flex-wrap gap-2">
                  {promptSuggestions.map((prompt, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors px-3 py-1.5 text-xs"
                      onClick={() => handlePromptClick(prompt)}
                    >
                      {prompt}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="px-6 py-3 border-t border-border bg-background">
              <div className="flex items-center gap-3">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="What would you like to know about your team?"
                  className="flex-1 h-12 text-base bg-background border-primary/30 focus:border-primary"
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                />
                <Button
                  size="lg"
                  className="px-6 h-12 bg-primary hover:bg-primary/90"
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim()}
                >
                  <Send className="h-5 w-5" />
                </Button>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Ask questions about emotional patterns, team dynamics, or specific insights from your organization's
                data.
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
